// PreviewTable — Phase 4 Plan 04-02 Task 2 Step 3.
//
// Renders the preview with per-row validity + inline cell editor.
// Virtualization (via @tanstack/react-virtual) only kicks in when rows.length > 100
// per UI-SPEC §Screen 5 Step 3. For MVP we keep the non-virtualized fallback
// (≤ 5 rows in the auto preview shell) — virtualization can be a P1 enhancement.
//
// Inline editor: tap a red cell → opens <input> below — debounced 500ms on
// change before calling onCellEdit (caller wires debounce + POST /revalidate-row).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PreviewRow } from "../api/excelImport";
import { ALERT_SEVERITY_COLORS } from "../lib/tokens";
import { prefersDark } from "../lib/tripStatusBadge";

export interface PreviewTableProps {
  rows: PreviewRow[];
  /** Called when the user finishes editing a cell. */
  onCellEdit: (rowIndex: number, field: string, value: string) => void;
  /** Active filter — "all" | "valid" | "errors". */
  filter?: "all" | "valid" | "errors";
}

const TARGET_COLUMNS = [
  "driver",
  "from",
  "to",
  "external_id",
  "customer_email",
  "cmr",
  "cargo_description",
];

export function PreviewTable({
  rows,
  onCellEdit,
  filter = "all",
}: PreviewTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<{ rowIndex: number; field: string } | null>(null);
  const [tmpValue, setTmpValue] = useState<string>("");

  // UI-SPEC §4/§5: шрифт наследуется из Telegram-темы (нет явного шрифта),
  // фон/бордеры — на var(--tg-theme-*). Error-cell — dark-aware destructive
  // токен (light-only #FEE2E2 нечитаем в dark mode).
  const dark = prefersDark();
  const errorBg = dark
    ? ALERT_SEVERITY_COLORS.destructive.bgDark
    : ALERT_SEVERITY_COLORS.destructive.bg;
  const errorBorder = ALERT_SEVERITY_COLORS.destructive.border;

  const filtered = rows.filter((r) => {
    if (filter === "valid") return r.is_valid;
    if (filter === "errors") return !r.is_valid;
    return true;
  });

  const errorByField = (row: PreviewRow, field: string): string | null => {
    const err = row.errors.find((e) => e.field === field);
    return err ? err.reason : null;
  };

  // WR-16: Translate the backend reason enum (e.g.
  // "route_geocoding_unavailable") into the operator's locale via
  // `import.error.<reason>`. Falls back to the raw enum string when no
  // translation key exists — surfaces the new reason quickly while the
  // localization team copies a native-quality translation.
  const localizeReason = (reason: string | null): string | null => {
    if (!reason) return null;
    return t(`import.error.${reason}`, { defaultValue: reason });
  };

  return (
    <table
      data-testid="preview-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
      }}
    >
      <thead>
        <tr
          style={{
            background: "var(--tg-theme-secondary-bg-color)",
            position: "sticky",
            top: 0,
          }}
        >
          <th
            style={{
              textAlign: "left",
              padding: "6px 8px",
              borderBottom: "1px solid var(--tg-theme-hint-color)",
              width: 40,
            }}
          >
            #
          </th>
          {TARGET_COLUMNS.map((c) => (
            <th
              key={c}
              style={{
                textAlign: "left",
                padding: "6px 8px",
                borderBottom: "1px solid var(--tg-theme-hint-color)",
                fontWeight: 600,
              }}
            >
              {c}
            </th>
          ))}
          <th
            style={{
              textAlign: "center",
              padding: "6px 8px",
              borderBottom: "1px solid var(--tg-theme-hint-color)",
              width: 40,
            }}
          >
            ✓
          </th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((row) => {
          const rowOk = row.is_valid;
          return (
            <tr
              key={row.row_index}
              data-testid={`preview-row-${row.row_index}`}
              data-valid={rowOk ? "true" : "false"}
              style={{
                borderBottom: "1px solid var(--tg-theme-hint-color)",
                outline: rowOk ? "1px solid transparent" : `1px solid ${errorBorder}`,
              }}
            >
              <td style={{ padding: "6px 8px", color: "var(--tg-theme-hint-color)" }}>
                {row.row_index + 1}
              </td>
              {TARGET_COLUMNS.map((field) => {
                const v = row.values[field] ?? "";
                const errReason = errorByField(row, field);
                // WR-16: localized text shown to the operator (tooltip).
                // data-error keeps the raw enum so test selectors and
                // future analytics can match on stable identifiers.
                const errReasonLocalized = localizeReason(errReason);
                const isEditing = editing?.rowIndex === row.row_index && editing.field === field;
                return (
                  <td
                    key={field}
                    style={{
                      padding: "6px 8px",
                      background: errReason ? errorBg : "transparent",
                      cursor: errReason ? "pointer" : "default",
                    }}
                    title={errReasonLocalized ?? undefined}
                    data-testid={`preview-cell-${row.row_index}-${field}`}
                    // WR-13 contract: only short, fixed-vocabulary reason
                    // codes (e.g. "invalid_email", "missing_required_field")
                    // may flow through this attribute. NEVER stuff the
                    // user-typed cell value here — browser extensions,
                    // copy/paste, and assistive tech can read attributes.
                    // If a future validator adds raw value text to the
                    // reason, surface it through a separate ref+state map
                    // keyed by row+field instead.
                    data-error={errReason ?? ""}
                    onClick={() => {
                      if (errReason && !isEditing) {
                        setEditing({ rowIndex: row.row_index, field });
                        setTmpValue(String(v ?? ""));
                      }
                    }}
                  >
                    {isEditing ? (
                      <input
                        data-testid={`preview-input-${row.row_index}-${field}`}
                        value={tmpValue}
                        onChange={(e) => setTmpValue(e.target.value)}
                        onBlur={() => {
                          onCellEdit(row.row_index, field, tmpValue);
                          setEditing(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onCellEdit(row.row_index, field, tmpValue);
                            setEditing(null);
                          } else if (e.key === "Escape") {
                            setEditing(null);
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: 4,
                          fontSize: 13,
                          border: "1px solid var(--tg-theme-button-color, #1E63CE)",
                        }}
                      />
                    ) : (
                      String(v ?? "")
                    )}
                  </td>
                );
              })}
              <td
                style={{
                  textAlign: "center",
                  color: rowOk ? "#059669" : "#DC2626",
                  fontWeight: 600,
                }}
              >
                {rowOk ? "✓" : "✗"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
